import type { Components } from '@mui/material/styles/components'
import type { Theme } from '@mui/material/styles'
import { createTheme } from '@mui/material/styles'
import { tableCellClasses } from '@mui/material/TableCell'
import { chipClasses } from '@mui/material/Chip'
import { inputLabelClasses } from '@mui/material/InputLabel'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { autocompleteClasses } from '@mui/material/Autocomplete'
import { switchClasses } from '@mui/material/Switch'
import { DENSITY, densityPx, densityPxStr } from './density'
import {
  FIL_SWITCH_DISABLED_OPACITY,
  filSwitchInset,
  getFilSwitchColors,
  getFilSwitchGeometry,
} from './fil-switch'
import { createAlertStyleOverrides } from './create-alert-styles'
import { badgeCssVariables } from '../badge-tokens'

// Used only to create transitions
const muiTheme = createTheme()

const px = densityPxStr

/**
 * Champ unique de l'app (TEC-1940) : outlined, libellé au-dessus du champ (`NfField`).
 * Valeurs en px réels, hors densité, comme la maquette du wizard « Nouveau dossier ».
 */
const FIELD_HEIGHT = 36
const FIELD_RADIUS = 8
const FIELD_PADDING_X = 12
const FIELD_FONT_SIZE = 11.5

/** Absente de `switchClasses`, posée par le ButtonBase du switch. */
const FOCUS_VISIBLE = 'Mui-focusVisible'

/**
 * Tiroirs (TEC-1956) : entrée rapide qui se pose en douceur, comme un ressort mais sans dépasser (un tiroir collé
 * au bord qui dépasse laisse voir un vide), sortie plus vive. Un tiroir qui passe ses propres `SlideProps` les
 * remplace : y reprendre `DRAWER_SLIDE_EASING`.
 */
export const DRAWER_SLIDE_EASING = {
  enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
}
export const DRAWER_TRANSITION_DURATION = { enter: 420, exit: 200 }

/** Glyphe checkbox un peu sous le 20px « logique » pour rester léger à côté des SvgIcon. */
const checkboxIconPx = densityPx(18)

export const createComponents = (): Components => {
  return {
    MuiAlert: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: createAlertStyleOverrides(),
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: densityPx(12), //fontSize: 14
          fontWeight: 600,
          letterSpacing: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: px(10),
          textTransform: 'none',
        },
        sizeSmall: {
          minHeight: densityPx(30),
          padding: `${px(4)} ${px(12)}`,
        },
        sizeMedium: {
          minHeight: densityPx(32),
          padding: `${px(5)} ${px(14)}`,
        },
        sizeLarge: {
          minHeight: densityPx(40),
          padding: `${px(8)} ${px(20)}`,
        },
        textSizeSmall: {
          minHeight: densityPx(30),
          padding: `${px(5)} ${px(10)}`,
        },
        textSizeMedium: {
          minHeight: densityPx(32),
          padding: `${px(6)} ${px(12)}`,
        },
        textSizeLarge: {
          minHeight: densityPx(40),
          padding: `${px(10)} ${px(14)}`,
        },
        containedWarning: {
          '& .MuiButton-startIcon': {
            color: '#DD8018',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: densityPx(12), //borderRadius: 20
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: `${px(32)} ${px(24)}`,
          '&:last-child': {
            paddingBottom: px(32),
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
        subheaderTypographyProps: {
          variant: 'body2',
        },
      },
      styleOverrides: {
        root: {
          padding: `${px(32)} ${px(24)} ${px(16)}`,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: (
          <svg
            fill="none"
            height={checkboxIconPx}
            viewBox="0 0 24 24"
            width={checkboxIconPx}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M9 3.5C5.68629 3.5 3 6.18629 3 9.5V15.5C3 18.8137 5.68629 21.5 9 21.5H15C18.3137 21.5 21 18.8137 21 15.5V9.5C21 6.18629 18.3137 3.5 15 3.5H9ZM16.7179 10.1961C17.1024 9.79966 17.0926 9.16657 16.6961 8.7821C16.2997 8.39763 15.6666 8.40737 15.2821 8.80385L10.6667 13.5635L8.7179 11.5539C8.33343 11.1574 7.70034 11.1476 7.30385 11.5321C6.90737 11.9166 6.89763 12.5497 7.2821 12.9461L9.94877 15.6961C10.1371 15.8904 10.3961 16 10.6667 16C10.9372 16 11.1962 15.8904 11.3846 15.6961L16.7179 10.1961Z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
        ),
        color: 'primary',
        icon: (
          <svg
            fill="none"
            height={checkboxIconPx}
            viewBox="0 0 24 24"
            width={checkboxIconPx}
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              height="13.333"
              rx="3.333"
              stroke="currentColor"
              strokeWidth="2"
              width="13.333"
              x="4.667"
              y="4.833"
            />
          </svg>
        ),
        indeterminateIcon: (
          <svg
            fill="none"
            height={checkboxIconPx}
            viewBox="0 0 24 24"
            width={checkboxIconPx}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M9 5.5H15C17.2091 5.5 19 7.29086 19 9.5V15.5C19 17.7091 17.2091 19.5 15 19.5H9C6.79086 19.5 5 17.7091 5 15.5V9.5C5 7.29086 6.79086 5.5 9 5.5ZM3 9.5C3 6.18629 5.68629 3.5 9 3.5H15C18.3137 3.5 21 6.18629 21 9.5V15.5C21 18.8137 18.3137 21.5 15 21.5H9C5.68629 21.5 3 18.8137 3 15.5V9.5ZM8 11.5C7.44772 11.5 7 11.9477 7 12.5C7 13.0523 7.44772 13.5 8 13.5H16C16.5523 13.5 17 13.0523 17 12.5C17 11.9477 16.5523 11.5 16 11.5H8Z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
        ),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          /* Pill explicite : évite l’aspect « carré » si shape ou hauteur custom ne coincidait pas */
          borderRadius: '9999px',
          [`&.${chipClasses.outlined}`]: {
            borderWidth: 0.5,
          },
        },
        sizeSmall: {
          height: densityPx(22),
          fontSize: densityPx(11),
          borderRadius: '9999px',
          '& .MuiChip-label': {
            paddingLeft: densityPx(6),
            paddingRight: densityPx(6),
          },
          '& .MuiChip-icon': {
            fontSize: px(14),
            marginLeft: px(4),
            marginRight: px(-2),
          },
          '& .MuiChip-deleteIcon': {
            fontSize: px(14),
            marginRight: px(4),
            marginLeft: px(-2),
          },
        },
        sizeMedium: {
          borderRadius: '9999px',
          '& .MuiChip-icon': {
            fontSize: px(16),
            marginLeft: px(6),
          },
          '& .MuiChip-deleteIcon': {
            fontSize: px(16),
            marginRight: px(5),
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ':root': badgeCssVariables,
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          // Ne pas utiliser CSS `zoom` ici : casse Popper (menus, tooltips, etc.)
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        '#root, #__next': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        },
        '#nprogress': {
          pointerEvents: 'none',
        },
        '#nprogress .bar': {
          height: densityPx(3),
          left: 0,
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 2000,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          padding: densityPx(4),
        },
        sizeMedium: {
          padding: densityPx(8),
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          fontSize: densityPx(12),
          fontWeight: 500,
          lineHeight: px(18),
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: densityPx(12),
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          borderRadius: densityPx(6),
          borderStyle: 'solid',
          borderWidth: 1,
          overflow: 'hidden',
          transition: muiTheme.transitions.create([
            'border-color',
            'box-shadow',
          ]),
          '&:before': {
            display: 'none',
          },
          '&:after': {
            display: 'none',
          },
        },
        input: {
          fontSize: densityPx(12),
          fontWeight: 500,
          lineHeight: px(18),
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: FIELD_RADIUS,
          transition: muiTheme.transitions.create(['box-shadow']),
          [`&:not(.${outlinedInputClasses.multiline})`]: {
            height: FIELD_HEIGHT,
          },
        },
        adornedStart: {
          paddingLeft: FIELD_PADDING_X,
        },
        adornedEnd: {
          paddingRight: FIELD_PADDING_X,
        },
        multiline: {
          padding: `8px ${FIELD_PADDING_X}px`,
        },
        input: {
          padding: `0 ${FIELD_PADDING_X}px`,
          fontSize: FIELD_FONT_SIZE,
          fontWeight: 500,
          lineHeight: px(24),
        },
        inputMultiline: {
          padding: 0,
        },
        inputAdornedStart: {
          paddingLeft: 0,
        },
        inputAdornedEnd: {
          paddingRight: 0,
        },
        notchedOutline: {
          transition: muiTheme.transitions.create([
            'border-color',
            'box-shadow',
          ]),
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: densityPx(12),
          fontWeight: 500,
          [`&.${inputLabelClasses.filled}`]: {
            transform: `translate(${px(12)}, ${px(18)}) scale(1)`,
          },
          [`&.${inputLabelClasses.outlined}`]: {
            transform: `translate(${FIELD_PADDING_X}px, 10px) scale(1)`,
          },
          [`&.${inputLabelClasses.shrink}`]: {
            [`&.${inputLabelClasses.standard}`]: {
              transform: `translate(0, ${densityPx(-1.5)}px) scale(0.85)`,
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: `translate(${px(12)}, ${px(6)}) scale(0.85)`,
            },
            [`&.${inputLabelClasses.outlined}`]: {
              transform: `translate(${px(14)}, ${px(-9)}) scale(0.85)`,
              background: 'transparent',
              marginLeft: '-1px',
            },
            [`&.${inputLabelClasses.outlined}.MuiFormLabel-filled`]: {
              transform: `translate(${px(14)}, ${px(-9)}) scale(0.85)`,
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: densityPx(3),
          overflow: 'hidden',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          marginRight: px(16),
          minWidth: 'unset',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    // Un seul arrondi pour toutes les modales, celui du wizard de création de dossier
    // (DialogUploadWizard) : ne pas le surcharger modale par modale.
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: densityPxStr(8),
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 16,
      },
    },
    MuiDrawer: {
      defaultProps: {
        transitionDuration: DRAWER_TRANSITION_DURATION,
        SlideProps: { easing: DRAWER_SLIDE_EASING },
      },
      styleOverrides: {
        // L'arrière-plan d'un tiroir temporaire se floute un peu (4 px), en fondu avec lui.
        modal: {
          '& .MuiBackdrop-root:not(.MuiBackdrop-invisible)': {
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          },
        },
        paper: {
          // `Slide` pose sa transition en style inline.
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none !important',
          },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'primary',
      },
    },
    // Switch « Fil », le même que `TraditionalSwitch` (`fil-switch.ts`).
    MuiSwitch: {
      defaultProps: {
        color: 'primary',
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          const g = getFilSwitchGeometry(
            ownerState.size === 'small' ? 'small' : 'medium'
          )
          return {
            width: g.trackW,
            height: g.trackH,
            padding: 0,
            margin: 8,
            overflow: 'visible',
          }
        },
        switchBase: ({ ownerState, theme }) => {
          const g = getFilSwitchGeometry(
            ownerState.size === 'small' ? 'small' : 'medium'
          )
          const c = getFilSwitchColors(theme as Theme)
          const track = `& + .${switchClasses.track}`
          return {
            padding: 0,
            top: g.pad,
            left: g.pad,
            color: c.line,
            '&:hover': { backgroundColor: 'transparent', color: c.lineHover },
            [`&:hover + .${switchClasses.track}`]: {
              boxShadow: filSwitchInset(c.lineHover),
            },
            [`&.${FOCUS_VISIBLE} + .${switchClasses.track}`]: {
              boxShadow: `${filSwitchInset(c.line)}, ${c.focusRing}`,
            },
            [`&.${switchClasses.checked}`]: {
              transform: `translateX(${g.travel}px)`,
              color: c.accent,
              [track]: {
                opacity: 1,
                backgroundColor: c.fill,
                boxShadow: filSwitchInset(c.accent),
              },
              '&:hover': {
                backgroundColor: 'transparent',
                color: c.accentHover,
              },
              [`&:hover + .${switchClasses.track}`]: {
                boxShadow: filSwitchInset(c.accentHover),
              },
              [`&.${FOCUS_VISIBLE} + .${switchClasses.track}`]: {
                boxShadow: `${filSwitchInset(c.accent)}, ${c.focusRing}`,
              },
            },
            [`&.${switchClasses.disabled}`]: {
              color: c.line,
              [`& .${switchClasses.thumb}`]: {
                opacity: FIL_SWITCH_DISABLED_OPACITY,
              },
              [track]: { opacity: FIL_SWITCH_DISABLED_OPACITY },
            },
            [`&.${switchClasses.checked}.${switchClasses.disabled}`]: {
              color: c.accent,
            },
          }
        },
        thumb: ({ ownerState }) => {
          const g = getFilSwitchGeometry(
            ownerState.size === 'small' ? 'small' : 'medium'
          )
          return {
            width: g.thumbW,
            height: g.thumbH,
            borderRadius: g.thumbH / 2,
            boxShadow: 'none',
          }
        },
        track: ({ ownerState, theme }) => {
          const g = getFilSwitchGeometry(
            ownerState.size === 'small' ? 'small' : 'medium'
          )
          return {
            borderRadius: g.trackH / 2,
            opacity: 1,
            backgroundColor: 'transparent',
            boxShadow: filSwitchInset(getFilSwitchColors(theme as Theme).line),
            transition: muiTheme.transitions.create(
              ['background-color', 'box-shadow'],
              {
                duration: muiTheme.transitions.duration.shorter,
              }
            ),
          }
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          // Un cran sous les 24px MUI de base (toujours × densité via densityPx)
          fontSize: px(21),
        },
        fontSizeSmall: {
          fontSize: px(18),
        },
        fontSizeLarge: {
          fontSize: px(32),
        },
        fontSizeInherit: {
          fontSize: 'inherit',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: densityPx(12), //fontSize: 14
          fontWeight: 500,
          lineHeight: 1.6, //lineHeight: 1.71
          minWidth: 'auto',
          paddingLeft: 0,
          paddingRight: 0,
          textTransform: 'none',
          '& + &': {
            marginLeft: densityPx(24),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: densityPx(12),
          padding: `${px(10)} ${px(12)}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
            fontSize: densityPx(12),
            fontWeight: 600,
            lineHeight: 0.8,
            letterSpacing: Math.round(0.4 * DENSITY * 100) / 100,
          },
          [`& .${tableCellClasses.paddingCheckbox}`]: {
            paddingTop: densityPx(4),
            paddingBottom: densityPx(4),
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          paddingLeft: px(8),
          paddingRight: px(8),
        },
        toolbar: {
          minHeight: densityPx(44),
        },
        selectLabel: {
          fontSize: densityPx(12),
        },
        displayedRows: {
          fontSize: densityPx(12),
        },
        actions: {
          marginLeft: densityPx(8),
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        // Toute la hauteur du champ est cliquable, et une valeur rendue en composant reste centrée.
        outlined: {
          height: FIELD_HEIGHT,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Le rembourrage propre à l'Autocomplete rendrait son champ plus haut que les autres.
        inputRoot: {
          [`&.${outlinedInputClasses.root}`]: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: FIELD_PADDING_X,
            [`& .${autocompleteClasses.input}`]: {
              padding: 0,
            },
            // Les étiquettes d'une sélection multiple passent à la ligne au lieu d'être coupées.
            [`&:has(.${autocompleteClasses.tag})`]: {
              height: 'auto',
              minHeight: FIELD_HEIGHT,
            },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
      },
    },
  }
}
